import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export const useNotifications = (userId: string | undefined) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    const fetchNotifications = async () => {
      try {
        const { data, error } = await supabase
          .from("notifications")
          .select("*")
          .eq("user_id", userId)
          .order("created_at", { ascending: false });

        if (error) throw error;

        setNotifications(data || []);
        setUnreadCount(data?.filter((n) => !n.read).length || 0);
      } catch (error) {
        console.error("Error fetching notifications:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();

    // Subscribe to realtime updates
    const channel = supabase
      .channel(`notifications-${userId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          console.log('🔔 Realtime notification event received:', payload);
          
          // Handle different event types
          if (payload.eventType === 'INSERT') {
            const newNotification = payload.new;
            console.log('➕ New notification inserted:', newNotification);
            
            // Add new notification to the top
            setNotifications((prev) => {
              // Avoid duplicates
              if (prev.some(n => n.id === newNotification.id)) {
                return prev;
              }
              return [newNotification, ...prev];
            });
            
            // Increment unread count if notification is unread
            if (!newNotification.read) {
              setUnreadCount((prev) => prev + 1);
            }
          } else if (payload.eventType === 'UPDATE') {
            console.log('✏️ Notification updated:', payload.new);
            const updatedNotif = payload.new;
            
            // Update notification and recalculate unread count atomically
            setNotifications((prev) => {
              const oldNotif = prev.find(n => n.id === updatedNotif.id);
              
              // Update unread count if read status changed
              if (oldNotif) {
                if (!oldNotif.read && updatedNotif.read) {
                  setUnreadCount((c) => Math.max(0, c - 1));
                } else if (oldNotif.read && !updatedNotif.read) {
                  setUnreadCount((c) => c + 1);
                }
              }
              
              return prev.map((n) => (n.id === updatedNotif.id ? updatedNotif : n));
            });
          } else if (payload.eventType === 'DELETE') {
            console.log('🗑️ Notification deleted:', payload.old);
            const deletedNotif = payload.old;
            
            // Remove notification and update unread count
            setNotifications((prev) => {
              if (!deletedNotif.read) {
                setUnreadCount((c) => Math.max(0, c - 1));
              }
              return prev.filter((n) => n.id !== deletedNotif.id);
            });
          }
        }
      )
      .subscribe((status, err) => {
        console.log('📡 Realtime subscription status:', status);
        
        if (status === 'SUBSCRIBED') {
          console.log('✅ Successfully subscribed to notifications channel');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('❌ Realtime subscription error:', err);
          // Optionally retry connection after delay
          if (userId) {
            console.log('🔄 Will retry notification subscription on next render');
          }
        } else if (status === 'TIMED_OUT') {
          console.error('⏱️ Realtime subscription timed out');
        } else if (status === 'CLOSED') {
          console.log('🔌 Realtime channel closed');
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId]);

  const markAsRead = async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from("notifications")
        .update({ read: true })
        .eq("id", notificationId);

      if (error) throw error;

      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const { error } = await supabase
        .from("notifications")
        .update({ read: true })
        .eq("user_id", userId)
        .eq("read", false);

      if (error) throw error;

      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
    }
  };

  const deleteNotification = async (notificationId: string) => {
    try {
      const notification = notifications.find(n => n.id === notificationId);
      const wasUnread = notification && !notification.read;

      const { error } = await supabase
        .from("notifications")
        .delete()
        .eq("id", notificationId);

      if (error) throw error;

      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      if (wasUnread) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error("Error deleting notification:", error);
      throw error;
    }
  };

  const deleteMultipleNotifications = async (notificationIds: string[]) => {
    try {
      const unreadToDelete = notifications.filter(
        n => notificationIds.includes(n.id) && !n.read
      ).length;

      const { error } = await supabase
        .from("notifications")
        .delete()
        .in("id", notificationIds);

      if (error) throw error;

      setNotifications((prev) => prev.filter((n) => !notificationIds.includes(n.id)));
      setUnreadCount((prev) => Math.max(0, prev - unreadToDelete));
    } catch (error) {
      console.error("Error deleting multiple notifications:", error);
      throw error;
    }
  };

  const markMultipleAsRead = async (notificationIds: string[]) => {
    try {
      const { error } = await supabase
        .from("notifications")
        .update({ read: true })
        .in("id", notificationIds)
        .eq("read", false);

      if (error) throw error;

      const unreadToMark = notifications.filter(
        n => notificationIds.includes(n.id) && !n.read
      ).length;

      setNotifications((prev) =>
        prev.map((n) =>
          notificationIds.includes(n.id) ? { ...n, read: true } : n
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - unreadToMark));
    } catch (error) {
      console.error("Error marking multiple notifications as read:", error);
      throw error;
    }
  };

  const getRecentNotifications = (limit: number = 5) => {
    return notifications.slice(0, limit);
  };

  return {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteMultipleNotifications,
    markMultipleAsRead,
    getRecentNotifications,
  };
};
